import { useCallback, useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Spinner, toast } from "@heroui/react";
import { asSearchString } from "@repo/utils/search-params";
import type { SendReceiveHistoryType } from "@repo/api";
import HistoryFilterBar from "@repo/ui/pages/template_service/history/HistoryFilterBar";
import HistoryRecordItem from "@repo/ui/pages/template_service/history/HistoryRecordItem";
import { useSendReceiveHistory } from "@repo/ui/pages/template_service/history/useSendReceiveHistory";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";
import { InfiniteScroll } from "@/components/infinite-scroll";

export type HistorySearch = {
  uid?: string;
  name?: string;
};

export function historyValidateSearch(search: Record<string, unknown>): HistorySearch {
  return {
    uid: asSearchString(search.uid),
    name: asSearchString(search.name),
  };
}

type HistorySharePageProps = {
  type: SendReceiveHistoryType;
  routeId: "/template_protections/history/received" | "/template_protections/history/sent";
};

export default function HistorySharePage({ type, routeId }: HistorySharePageProps) {
  const routeApi = getRouteApi(routeId);
  const { uid: uidParam, name: nameParam } = routeApi.useSearch();
  const routeNavigate = routeApi.useNavigate();
  const navigate = useNavigate();

  const [uidInput, setUidInput] = useState(uidParam ?? "");
  const [nameInput, setNameInput] = useState(nameParam ?? "");

  const uid = uidParam ?? "";
  const name = nameParam ?? "";

  const onError = useCallback((message: string) => {
    toast.danger(message);
  }, []);

  const {
    items,
    loading,
    loadingMore,
    hasMore,
    shareDenied,
    loadMore,
    resolveParty,
  } = useSendReceiveHistory({
    type,
    uid,
    name,
    pageSize: 20,
    enabled: true,
    onError,
  });

  const applySearch = useCallback(() => {
    void routeNavigate({
      search: {
        uid: uidInput.trim() || undefined,
        name: nameInput.trim() || undefined,
      },
    });
  }, [routeNavigate, uidInput, nameInput]);

  return (
    <SafeAreaContainer className="bg-surface" customPaddingBottom="2rem">
      <div className="flex flex-col gap-3 px-4 pt-3">
        <HistoryFilterBar
          type={type}
          uid={uidInput}
          name={nameInput}
          onUidChange={(value) => {
            setUidInput(value);
            if (value === "") {
              void routeNavigate({
                search: (prev) => ({ ...prev, uid: undefined }),
              });
            }
          }}
          onNameChange={(value) => {
            setNameInput(value);
            if (value === "") {
              void routeNavigate({
                search: (prev) => ({ ...prev, name: undefined }),
              });
            }
          }}
          onSubmit={applySearch}
        />

        {shareDenied ? (
          <div className="py-12 text-center text-gray-500 text-sm">
            无权查看该记录
          </div>
        ) : loading && items.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <InfiniteScroll
            loading={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadingText="加载中"
            noMoreText="没有更多了"
          >
            {items.length === 0 && !loading ? (
              <div className="py-12 text-center text-gray-500 text-sm">暂无记录</div>
            ) : (
              items.map((item) => (
                <HistoryRecordItem
                  key={`${item.send_id}-${item.template_id}-${item.received_at}`}
                  item={item}
                  type={type}
                  party={resolveParty(item)}
                  onUserPress={(partyUid) =>
                    navigate({
                      to: "/template_protections/trusted-query",
                      search: { search: partyUid },
                    })
                  }
                  onTemplatePress={(templateId, sendId) =>
                    navigate({
                      to: "/template_protections/detail/$templateId",
                      params: { templateId },
                      search: { sendId: sendId != null ? String(sendId) : undefined },
                    })
                  }
                />
              ))
            )}
          </InfiniteScroll>
        )}
      </div>
    </SafeAreaContainer>
  );
}
